import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import EmojiPicker from 'emoji-picker-react';
import { jwtDecode } from 'jwt-decode';
import PollViewer from '../components/PollViewer';
import { FaReply, FaPaperPlane, FaTimes, FaEdit, FaTrash, FaEye, FaEyeSlash, FaPalette, FaRuler, FaSmile, FaLock, FaLink } from 'react-icons/fa';
import FaQuote from '../components/FaQuote';
import { trackThreadView } from '../utils/analytics';

function PostList() {
  const { threadId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page')) || 1;

  const [thread, setThread] = useState({});
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState({});
  const [editingPostId, setEditingPostId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUser({
          id: decoded.id,
          isAdmin: decoded.isAdmin,
          groups: decoded.groups || [],
        });
      } catch {
        setCurrentUser({});
      }
    }
  }, []);

  const canModerate = () =>
    currentUser.isAdmin ||
    currentUser.groups?.some(g => g.permissions?.canModerate);

  const formAllowed = !!currentUser.id && (!thread.locked || canModerate());

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToForm = () => {
    setTimeout(() => {
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/posts/${threadId}`, {
        params: { page, limit }
      });
      const threadData = res.data.thread || {};
      setThread(threadData);
      setPosts(res.data.posts);
      setTotalPages(res.data.pagination.totalPages || 1);
      
      // Track thread view with title
      if (threadData.title) {
        trackThreadView(threadId, threadData.title, page);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [threadId, page]);

  useEffect(() => {
    loadPosts().then(() => {
      if (currentUser.id) {
        axiosInstance
          .patch(`/thread-activity/mark-read/${threadId}`)
          .catch(console.error);
      }

      // Check for unread parameter in URL
      const isUnreadNavigation = searchParams.get('unread') === 'true';
      const hash = window.location.hash;
      
      if (isUnreadNavigation) {
        // Handle unread navigation
        setTimeout(() => {
          handleUnreadNavigation();
        }, 100);
      } else if (hash?.startsWith('#')) {
        setTimeout(() => {
          const target = document.getElementById(hash.slice(1));
          if (target) {
            // If it's a post anchor, scroll to it with better positioning
            if (hash.startsWith('#post-')) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (hash === '#lastpost') {
              // Navigate to the last post on the page
              const lastPost = document.querySelector('.post-card:last-child');
              if (lastPost) {
                lastPost.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } else {
                // Fall back to bottom of page if no posts found
                endRef.current?.scrollIntoView({ behavior: 'smooth' });
              }
            } else {
              // Generic target
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } else if (hash === '#lastpost') {
            // If no specific target, scroll to the last post
            const lastPost = document.querySelector('.post-card:last-child');
            if (lastPost) {
              lastPost.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
              endRef.current?.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }, 100);
      }
    });
  }, [loadPosts, threadId, currentUser.id, searchParams]);

  // Handle navigation to first unread post
  const handleUnreadNavigation = async () => {
    try {
      // Get user's last read information for this thread
      const res = await axiosInstance.get(`/thread-activity/last-read/${threadId}`);
      const lastReadPostId = res.data.lastReadPostId;
      
      if (lastReadPostId) {
        // First, try to find the last read post on the current page
        const currentPostIndex = posts.findIndex(post => post._id === lastReadPostId);          if (currentPostIndex >= 0) {
            // Last read post is on current page
            if (currentPostIndex < posts.length - 1) {
              // There's a next post on this page (first unread)
              const nextPost = posts[currentPostIndex + 1];
              const nextPostElement = document.getElementById(`post-${nextPost._id}`);
              if (nextPostElement) {
                nextPostElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
              }
            } else {
              // Last read post is the last post on current page, go to next page
              if (page < totalPages) {
                const nextPage = page + 1;
                setPage(nextPage);
                setSearchParams({ page: nextPage.toString() });
                // Wait for the page to load before scrolling to first post
                setTimeout(() => {
                  const firstPost = document.querySelector('.post-card:first-child');
                  if (firstPost) {
                    firstPost.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 500);
                return;
              }
            }
          } else {
            // Last read post is not on current page
            // Check if we should go to an earlier page or later page
            const firstPostOnPage = posts[0];
            const lastPostOnPage = posts[posts.length - 1];
            
            // Compare creation times to determine direction
            if (firstPostOnPage && new Date(firstPostOnPage.createdAt) > new Date(res.data.lastReadAt)) {
              // Current page is after the last read post, so first post is unread
              const firstPost = document.querySelector('.post-card:first-child');
              if (firstPost) {
                firstPost.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              return;
            } else if (lastPostOnPage && new Date(lastPostOnPage.createdAt) < new Date(res.data.lastReadAt)) {
              // Current page is before the last read post, go to next page
              if (page < totalPages) {
                const nextPage = page + 1;
                setPage(nextPage);
                setSearchParams({ page: nextPage.toString() });
                setTimeout(() => {
                  const firstPost = document.querySelector('.post-card:first-child');
                  if (firstPost) {
                    firstPost.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 500);
                return;
              }
            }
          }
        }
        
        // If no specific unread post found, scroll to first post on current page
        const firstPost = document.querySelector('.post-card:first-child');
        if (firstPost) {
          firstPost.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (err) {
        console.error('Error navigating to unread post:', err);
        // Fall back to first post
        const firstPost = document.querySelector('.post-card:first-child');
        if (firstPost) {
          firstPost.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    setSearchParams({ page: newPage.toString() }, { replace: true });
  };

  const handleEmojiClick = emojiData => {
    setContent(prev => prev + emojiData.emoji);
  };

  const insertBBCode = tag => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent(prev => `${prev}[${tag}][/${tag.split('=')[0]}]`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const tagName = tag.split('=')[0]; // Handle tags like 'color=red'
    
    let newContent;
    if (selectedText) {
      // Wrap selected text with BBCode tags
      const beforeSelection = content.substring(0, start);
      const afterSelection = content.substring(end);
      newContent = `${beforeSelection}[${tag}]${selectedText}[/${tagName}]${afterSelection}`;
    } else {
      // No selection, insert empty tags at cursor position
      const beforeCursor = content.substring(0, start);
      const afterCursor = content.substring(start);
      newContent = `${beforeCursor}[${tag}][/${tagName}]${afterCursor}`;
    }
    
    setContent(newContent);
    
    // Restore cursor position after the closing tag
    setTimeout(() => {
      const newCursorPos = selectedText 
        ? start + tag.length + 2 + selectedText.length + tagName.length + 3
        : start + tag.length + 2;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const insertLink = () => {
    const url = prompt('URL eingeben:');
    if (!url) return;
    
    const textarea = textareaRef.current;
    if (!textarea) {
      setContent(prev => `${prev}[link=${url}]Link-Text[/link]`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newContent;
    if (selectedText) {
      // Wrap selected text with link BBCode
      const beforeSelection = content.substring(0, start);
      const afterSelection = content.substring(end);
      newContent = `${beforeSelection}[link=${url}]${selectedText}[/link]${afterSelection}`;
    } else {
      // No selection, insert link with placeholder text
      const beforeCursor = content.substring(0, start);
      const afterCursor = content.substring(start);
      newContent = `${beforeCursor}[link=${url}]Link-Text[/link]${afterCursor}`;
    }
    
    setContent(newContent);
    
    // Restore cursor position
    setTimeout(() => {
      if (selectedText) {
        // Place cursor after the link
        const newCursorPos = start + `[link=${url}]${selectedText}[/link]`.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      } else {
        // Select the placeholder text so user can replace it
        const linkStart = start + `[link=${url}]`.length;
        const linkEnd = linkStart + 'Link-Text'.length;
        textarea.setSelectionRange(linkStart, linkEnd);
      }
      textarea.focus();
    }, 0);
  };

  const insertLinkEdit = () => {
    const url = prompt('URL eingeben:');
    if (!url) return;
    
    const textarea = document.querySelector('.edit-textarea');
    if (!textarea) {
      setEditContent(prev => `${prev}[link=${url}]Link-Text[/link]`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = editContent.substring(start, end);
    
    let newContent;
    if (selectedText) {
      // Wrap selected text with link BBCode
      const beforeSelection = editContent.substring(0, start);
      const afterSelection = editContent.substring(end);
      newContent = `${beforeSelection}[link=${url}]${selectedText}[/link]${afterSelection}`;
    } else {
      // No selection, insert link with placeholder text
      const beforeCursor = editContent.substring(0, start);
      const afterCursor = editContent.substring(start);
      newContent = `${beforeCursor}[link=${url}]Link-Text[/link]${afterCursor}`;
    }
    
    setEditContent(newContent);
    
    // Restore cursor position
    setTimeout(() => {
      if (selectedText) {
        // Place cursor after the link
        const newCursorPos = start + `[link=${url}]${selectedText}[/link]`.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      } else {
        // Select the placeholder text so user can replace it
        const linkStart = start + `[link=${url}]`.length;
        const linkEnd = linkStart + 'Link-Text'.length;
        textarea.setSelectionRange(linkStart, linkEnd);
      }
      textarea.focus();
    }, 0);
  };

  const handleSubmit = e => {
    e.preventDefault();
    axiosInstance
      .post(`/posts/${threadId}`, { content })
      .then(() => {
        setContent('');
        setShowForm(false);
        loadPosts();
      })
      .catch(console.error);
  };

  const handleQuote = post => {
    setShowForm(true);
    const quoteContent = `[quote author="${post.author?.username}" date="${new Date(
      post.createdAt
    ).toLocaleString()}"]${post.contentBBCode}[/quote]
`;
    setContent(prev => prev + quoteContent);
    scrollToForm();
  };

  const handleDelete = postId => {
    if (window.confirm('Möchtest du diesen Beitrag wirklich löschen?')) {
      axiosInstance.delete(`/posts/${postId}`).then(loadPosts);
    }
  };

  const handleToggleInvisible = postId => {
    axiosInstance.patch(`/posts/toggle-invisible/${postId}`).then(loadPosts);
  };

  const handleEditClick = post => {
    setEditingPostId(post._id);
    setEditContent(post.contentBBCode);
  };

  const handleEditSubmit = async postId => {
    try {
      await axiosInstance.patch(`/posts/${postId}`, { content: editContent });
      setEditingPostId(null);
      loadPosts();
    } catch {
      alert('Fehler beim Aktualisieren');
    }
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxVisible = 7;
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, page + half);

    if (page <= half) {
      end = Math.min(totalPages, maxVisible);
    }
    if (page + half >= totalPages) {
      start = Math.max(1, totalPages - maxVisible + 1);
    }

    if (start > 1) pageNumbers.push('prevDots');
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    if (end < totalPages) pageNumbers.push('nextDots');

    return (
      <div className="flex flex-wrap justify-center gap-1 mt-6 mb-10">
        <button className="btn btn-sm" disabled={page === 1} onClick={() => handlePageChange(Math.max(page - 1, 1))}>‹</button>
        {pageNumbers.map((num, idx) =>
          num === 'prevDots' ? (
            <span key={`pd-${idx}`} className="btn btn-sm btn-ghost cursor-default">…</span>
          ) : num === 'nextDots' ? (
            <span key={`nd-${idx}`} className="btn btn-sm btn-ghost cursor-default">…</span>
          ) : (
            <button
              key={num}
              className={`btn btn-sm ${page === num ? 'bg-[#448f65] text-white' : 'bg-white text-[#448f65] border border-[#448f65]'}`}
              onClick={() => handlePageChange(num)}
            >
              {num}
            </button>
          )
        )}
        {page < totalPages && (
          <button
            className="btn btn-sm btn-outline text-[#448f65]"
            onClick={() => handlePageChange(totalPages)}
          >
            Letzte
          </button>
        )}
        <button className="btn btn-sm" disabled={page === totalPages} onClick={() => handlePageChange(Math.min(page + 1, totalPages))}>›</button>
      </div>
    );
  };
  return (
    <div className="min-h-screen">
      <div className="w-full px-2 sm:px-4 space-y-6">
      {/* Thread Header */}
      <div className="modern-card p-6">
        <h1 className="text-3xl font-bold mb-2 !text-white" style={{ color: 'white !important', WebkitTextFillColor: 'white !important' }}>{thread.title || 'Beiträge'}</h1>
        {thread.description && (
          <p className="text-gray-600 dark:text-gray-300 text-sm">{thread.description}</p>
        )}
        {thread.locked && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-100 dark:bg-yellow-200 text-gray-900 dark:text-gray-800 text-sm mt-2">
            <FaLock className="w-3 h-3" />
            Gesperrt
          </div>
        )}
      </div>

      {/* Poll Section */}
      {thread.isPoll && (
        <div className="modern-card p-6">
          <h3 className="font-bold text-xl mb-4 text-gray-900 dark:text-white">{thread.poll?.question}</h3>
          <PollViewer threadId={thread._id} />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          {/* Posts List */}
          <div className="space-y-6">
            {posts.map(post => (!post.invisible || currentUser.isAdmin || currentUser.id === post.author?._id) && (
              <article key={post._id} className="modern-card p-6 post-card" id={`post-${post._id}`}>
                {/* Post Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="avatar-wrapper">
                    {post.author?.avatarUrl ? (
                      <img 
                        src={`https://forumapi.more-features.com${post.author.avatarUrl}`}  
                        alt="Avatar"
                        className="w-12 h-12 rounded-full object-cover border-2 border-primary/30" 
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold">
                        {post.author?.username?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-white">{post.author?.username || 'Unbekannt'}</span>
                      {post.author?.isAdmin && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-300 text-xs rounded-full border border-red-500/30">
                          Admin
                        </span>
                      )}
                      {post.invisible && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full border border-amber-500/30">
                          Versteckt
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400">
                      {new Date(post.createdAt).toLocaleString('de-DE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {post.editedAt && post.editedAt !== post.createdAt && (
                        <span className="ml-2 text-xs text-gray-500">
                          (bearbeitet: {new Date(post.editedAt).toLocaleString('de-DE')})
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Post Content */}
                <div className="post-content">
                  {editingPostId === post._id ? (
                    <div className="space-y-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <FaEdit className="w-4 h-4 text-primary-green" />
                        Beitrag bearbeiten
                      </h4>
                      
                      {/* BBCode Toolbar */}
                      <div className="flex flex-wrap gap-2 p-4 bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                        {[
                          { code: 'b', label: 'B', title: 'Fett' },
                          { code: 'i', label: 'I', title: 'Kursiv' },
                          { code: 'u', label: 'U', title: 'Unterstrichen' },
                          { code: 's', label: 'S', title: 'Durchgestrichen' }
                        ].map(tag => (
                          <button 
                            type="button" 
                            key={tag.code} 
                            className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                            title={tag.title}
                            onClick={() => {
                              const textarea = document.querySelector('.edit-textarea');
                              const start = textarea.selectionStart;
                              const end = textarea.selectionEnd;
                              const selectedText = editContent.substring(start, end);
                              const newText = editContent.substring(0, start) + `[${tag.code}]${selectedText}[/${tag.code}]` + editContent.substring(end);
                              setEditContent(newText);
                            }}
                          >
                            {tag.label}
                          </button>
                        ))}
                        <button 
                          type="button" 
                          className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                          title="Farbe (rot)"
                          onClick={() => {
                            const textarea = document.querySelector('.edit-textarea');
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const selectedText = editContent.substring(start, end);
                            const newText = editContent.substring(0, start) + `[color=red]${selectedText}[/color]` + editContent.substring(end);
                            setEditContent(newText);
                          }}
                        >
                          <FaPalette className="w-3 h-3" />
                        </button>
                        <button 
                          type="button" 
                          className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                          title="Größe (18pt)"
                          onClick={() => {
                            const textarea = document.querySelector('.edit-textarea');
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const selectedText = editContent.substring(start, end);
                            const newText = editContent.substring(0, start) + `[size=18]${selectedText}[/size]` + editContent.substring(end);
                            setEditContent(newText);
                          }}
                        >
                          <FaRuler className="w-3 h-3" />
                        </button>
                        <button 
                          type="button" 
                          className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                          title="Link einfügen"
                          onClick={insertLinkEdit}
                        >
                          <FaLink className="w-3 h-3" />
                        </button>
                        <button 
                          type="button" 
                          className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                          title="Emoji hinzufügen"
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        >
                          <FaSmile className="w-3 h-3" />
                        </button>
                        <button 
                          type="button" 
                          className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                          title="Zitat einfügen"
                          onClick={() => insertBBCode('quote')}
                        >
                          <FaQuote className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Content Textarea */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Inhalt</label>
                        <textarea 
                          rows={8} 
                          value={editContent} 
                          onChange={e => setEditContent(e.target.value)} 
                          className="edit-textarea w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[200px] resize-y"
                          placeholder="Bearbeite deinen Beitrag..."
                        />
                        
                        {/* Emoji Picker */}
                        {showEmojiPicker && (
                          <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
                            <EmojiPicker
                              onEmojiClick={(emojiObject) => setEditContent(prev => prev + emojiObject.emoji)}
                              theme="auto"
                              emojiStyle="native"
                              previewConfig={{ showPreview: false }}
                              width="100%"
                              height={300}
                            />
                          </div>
                        )}
                      </div>

                      {/* Form Actions */}
                      <div className="flex gap-4 pt-4">
                        <button 
                          className="glass-btn px-6 py-3 text-white font-medium rounded-xl flex items-center gap-2" 
                          onClick={() => handleEditSubmit(post._id)}
                        >
                          <FaPaperPlane className="w-4 h-4" />
                          Speichern
                        </button>
                        <button 
                          className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center gap-2" 
                          onClick={() => {
                            setEditingPostId(null);
                            setShowEmojiPicker(false);
                          }}
                        >
                          <FaTimes className="w-4 h-4" />
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: post.contentHtml }} 
                    />
                  )}
                </div>

                {/* Signature */}
                {post.author?.signatureHtml && (
                  <div className="mt-6 pt-4 border-t border-white/10">
                    <div 
                      className="text-sm text-gray-400 signature"
                      dangerouslySetInnerHTML={{ __html: post.author.signatureHtml }} 
                    />
                  </div>
                )}

                {/* Post Actions */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {!post.invisible && (
                    <button 
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200 rounded-lg text-sm font-medium flex items-center gap-1" 
                      onClick={() => handleQuote(post)}
                    >
                      <FaQuote className="w-3 h-3" />
                      Zitieren
                    </button>
                  )}
                  {(currentUser.isAdmin || currentUser.id === post.author?._id) && (
                    <>
                      <button 
                        className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium flex items-center gap-1" 
                        onClick={() => handleEditClick(post)}
                      >
                        <FaEdit className="w-3 h-3" />
                        Bearbeiten
                      </button>
                      <button 
                        className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 rounded-lg text-sm font-medium flex items-center gap-1" 
                        onClick={() => handleDelete(post._id)}
                      >
                        <FaTrash className="w-3 h-3" />
                        Löschen
                      </button>
                    </>
                  )}
                  {currentUser.isAdmin && (
                    <button 
                      className="px-3 py-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 border border-yellow-200 rounded-lg text-sm font-medium flex items-center gap-1" 
                      onClick={() => handleToggleInvisible(post._id)}
                    >
                      {post.invisible ? (
                        <>
                          <FaEye className="w-3 h-3" />
                          Einblenden
                        </>
                      ) : (
                        <>
                          <FaEyeSlash className="w-3 h-3" />
                          Verbergen
                        </>
                      )}
                    </button>
                  )}
                </div>
              </article>
            ))}
            <div ref={endRef} />
          </div>

          {/* Reply Form */}
          {showForm && formAllowed && (
            <div ref={inputRef} className="modern-card p-8 animate-fade-in w-full max-w-none">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <FaEdit className="w-5 h-5 text-primary-green" />
                Antwort verfassen
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* BBCode Toolbar */}
                <div className="flex flex-wrap gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  {[
                    { code: 'b', label: 'B', title: 'Fett' },
                    { code: 'i', label: 'I', title: 'Kursiv' },
                    { code: 'u', label: 'U', title: 'Unterstrichen' },
                    { code: 's', label: 'S', title: 'Durchgestrichen' }
                  ].map(tag => (
                    <button 
                      type="button" 
                      key={tag.code} 
                      className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                      title={tag.title}
                      onClick={() => insertBBCode(tag.code)}
                    >
                      {tag.label}
                    </button>
                  ))}
                  <button 
                    type="button" 
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                    title="Link einfügen"
                    onClick={insertLink}
                  >
                    <FaLink className="w-3 h-3" />
                  </button>
                  <button 
                    type="button" 
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                    title="Farbe (rot)"
                    onClick={() => insertBBCode('color=red')}
                  >
                    <FaPalette className="w-3 h-3" />
                  </button>
                  <button 
                    type="button" 
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                    title="Größe (18pt)"
                    onClick={() => insertBBCode('size=18')}
                  >
                    <FaRuler className="w-3 h-3" />
                  </button>
                  <button 
                    type="button" 
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                    title="Emoji hinzufügen"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <FaSmile className="w-3 h-3" />
                  </button>
                  <button 
                    type="button" 
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-1"
                    title="Zitat einfügen"
                    onClick={() => insertBBCode('quote')}
                  >
                    <FaQuote className="w-3 h-3" />
                  </button>
                </div>

                {/* Content Textarea */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Inhalt</label>
                  <textarea 
                    ref={textareaRef}
                    rows={8} 
                    value={content} 
                    onChange={e => setContent(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[200px] resize-y"
                    placeholder="Schreibe deine Antwort hier..."
                    required
                  />
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker && (
                    <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg">
                      <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        theme="auto"
                        emojiStyle="native"
                        previewConfig={{ showPreview: false }}
                        width="100%"
                        height={300}
                      />
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="glass-btn px-6 py-3 text-white font-medium rounded-xl flex items-center gap-2">
                    <FaPaperPlane className="w-4 h-4" />
                    Absenden
                  </button>
                  <button 
                    type="button" 
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center gap-2"
                    onClick={() => {
                      setShowForm(false);
                      setContent('');
                      setShowEmojiPicker(false);
                    }}
                  >
                    <FaTimes className="w-4 h-4" />
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Reply Button */}
          {formAllowed && !showForm && (
            <div className="text-center py-6">
              <button 
                onClick={() => { 
                  setShowForm(true); 
                  scrollToBottom(); 
                }} 
                className="glass-btn px-8 py-4 text-white font-semibold rounded-xl shadow-glow hover:shadow-xl transition-all duration-200 flex items-center gap-3 mx-auto"
              >
                <FaReply className="w-5 h-5" />
                Antworten
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-wrap justify-center gap-2 py-8">
              <button 
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page === 1} 
                onClick={() => handlePageChange(Math.max(page - 1, 1))}
              >
                ‹ Zurück
              </button>
              
              {(() => {
                const pageNumbers = [];
                const maxVisible = 7;
                const half = Math.floor(maxVisible / 2);
                let start = Math.max(1, page - half);
                let end = Math.min(totalPages, page + half);

                if (page <= half) {
                  end = Math.min(totalPages, maxVisible);
                }
                if (page + half >= totalPages) {
                  start = Math.max(1, totalPages - maxVisible + 1);
                }

                if (start > 1) {
                  pageNumbers.push(
                    <button key="first" className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => handlePageChange(1)}>
                      1
                    </button>
                  );
                  if (start > 2) {
                    pageNumbers.push(<span key="dots1" className="px-3 py-2 text-gray-400">…</span>);
                  }
                }

                for (let i = start; i <= end; i++) {
                  pageNumbers.push(
                    <button
                      key={i}
                      className={page === i 
                        ? 'px-4 py-2 bg-primary-green text-white rounded-lg font-medium' 
                        : 'px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                      }
                      onClick={() => handlePageChange(i)}
                    >
                      {i}
                    </button>
                  );
                }

                if (end < totalPages) {
                  if (end < totalPages - 1) {
                    pageNumbers.push(<span key="dots2" className="px-3 py-2 text-gray-400">…</span>);
                  }
                  pageNumbers.push(
                    <button key="last" className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => handlePageChange(totalPages)}>
                      {totalPages}
                    </button>
                  );
                }

                return pageNumbers;
              })()}
              
              <button 
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page === totalPages} 
                onClick={() => handlePageChange(Math.min(page + 1, totalPages))}
              >
                Weiter ›
              </button>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

export default PostList;
